import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { releasesApi } from '@/lib/api'
import type { AppRelease } from '@/types'
import { PackageOpen, Plus, Trash2, ExternalLink, FileText, X, Smartphone, Apple } from 'lucide-react'
import { formatDateTime } from '@/lib/utils'

type PlatformTab = 'all' | 'android' | 'ios'

const TABS: { id: PlatformTab; label: string; icon: React.ReactNode }[] = [
  { id: 'all',     label: 'Todas',   icon: <PackageOpen className="h-4 w-4" /> },
  { id: 'android', label: 'Android', icon: <Smartphone className="h-4 w-4" /> },
  { id: 'ios',     label: 'iOS',     icon: <Apple className="h-4 w-4" /> },
]

const EMPTY_FORM = { version: '', apk_url: '', ios_url: '', changelog: '' }

export default function ReleasesPage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<PlatformTab>('all')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [urlError, setUrlError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['releases'],
    queryFn: () => releasesApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: releasesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] })
      setShowForm(false)
      setForm(EMPTY_FORM)
      setUrlError('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: releasesApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['releases'] }),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.apk_url && !form.ios_url) {
      setUrlError('Introduza pelo menos um URL (Android APK ou iOS).')
      return
    }
    setUrlError('')
    createMutation.mutate({
      version: form.version,
      apk_url: form.apk_url || undefined,
      ios_url: form.ios_url || undefined,
      changelog: form.changelog || undefined,
    })
  }

  const allReleases: AppRelease[] = data?.data?.items ?? []

  const filtered = allReleases.filter((r) => {
    if (tab === 'android') return !!r.apk_url
    if (tab === 'ios')     return !!r.ios_url
    return true
  })

  const counts = {
    all:     allReleases.length,
    android: allReleases.filter((r) => !!r.apk_url).length,
    ios:     allReleases.filter((r) => !!r.ios_url).length,
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Versões da App</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestão de releases para Android e iOS
          </p>
        </div>
        <button
          onClick={() => { setShowForm(true); setUrlError('') }}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nova Versão
        </button>
      </div>

      {/* Platform tabs */}
      <div className="mt-5 flex gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t.id
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t.icon}
            {t.label}
            <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
              tab === t.id ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {counts[t.id]}
            </span>
          </button>
        ))}
      </div>

      {/* New Release Form */}
      {showForm && (
        <div className="mt-5 card border border-primary-200 bg-primary-50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Publicar Nova Versão</h2>
            <button onClick={() => { setShowForm(false); setUrlError('') }} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Version */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Versão <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="ex: 1.0.38"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                className="input w-48"
                required
              />
            </div>

            {/* Platform URLs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                  <Smartphone className="h-4 w-4 text-green-600" />
                  Android — URL do APK
                </label>
                <input
                  type="url"
                  placeholder="https://storage.blob.core.windows.net/.../app.apk"
                  value={form.apk_url}
                  onChange={(e) => { setForm({ ...form, apk_url: e.target.value }); setUrlError('') }}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                  <Apple className="h-4 w-4 text-blue-600" />
                  iOS — TestFlight / App Store URL
                </label>
                <input
                  type="url"
                  placeholder="https://testflight.apple.com/join/..."
                  value={form.ios_url}
                  onChange={(e) => { setForm({ ...form, ios_url: e.target.value }); setUrlError('') }}
                  className="input w-full"
                />
              </div>
            </div>
            {urlError && <p className="text-sm text-red-600">{urlError}</p>}

            {/* Changelog */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alterações / Changelog
              </label>
              <textarea
                placeholder="Descreva as alterações desta versão..."
                value={form.changelog}
                onChange={(e) => setForm({ ...form, changelog: e.target.value })}
                className="input w-full"
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => { setShowForm(false); setUrlError('') }} className="btn btn-secondary">
                Cancelar
              </button>
              <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
                {createMutation.isPending ? 'A publicar...' : 'Publicar Versão'}
              </button>
            </div>
            {createMutation.isError && (
              <p className="text-sm text-red-600">Erro ao publicar versão. Tente novamente.</p>
            )}
          </form>
        </div>
      )}

      {/* Releases Table */}
      <div className="mt-5 card">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <PackageOpen className="h-12 w-12 mb-3" />
            <p className="text-sm">Nenhuma versão publicada{tab !== 'all' ? ` para ${tab === 'android' ? 'Android' : 'iOS'}` : ''} ainda.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Versão</th>
                <th>
                  <span className="flex items-center gap-1">
                    <Smartphone className="h-3.5 w-3.5 text-green-600" /> Android
                  </span>
                </th>
                <th>
                  <span className="flex items-center gap-1">
                    <Apple className="h-3.5 w-3.5 text-blue-600" /> iOS
                  </span>
                </th>
                <th>Alterações</th>
                <th>Publicado por</th>
                <th>Data / Hora</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((release) => (
                <tr key={release.id}>
                  {/* Version badge */}
                  <td>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-sm font-semibold text-primary-800">
                      <PackageOpen className="h-3.5 w-3.5" />
                      v{release.version}
                    </span>
                  </td>

                  {/* Android APK */}
                  <td>
                    {release.apk_url ? (
                      <a
                        href={release.apk_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Descarregar APK
                      </a>
                    ) : (
                      <span className="text-gray-300 text-sm">—</span>
                    )}
                  </td>

                  {/* iOS */}
                  <td>
                    {release.ios_url ? (
                      <a
                        href={release.ios_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3" />
                        TestFlight / Store
                      </a>
                    ) : (
                      <span className="text-gray-300 text-sm">—</span>
                    )}
                  </td>

                  {/* Changelog */}
                  <td className="max-w-xs">
                    {release.changelog ? (
                      <details className="cursor-pointer">
                        <summary className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 list-none">
                          <FileText className="h-3.5 w-3.5" />
                          Ver alterações
                        </summary>
                        <pre className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-xs text-gray-700 border border-gray-200 max-h-48 overflow-y-auto">
                          {release.changelog}
                        </pre>
                      </details>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>

                  {/* Published by */}
                  <td className="text-sm text-gray-600">{release.published_by}</td>

                  {/* Date */}
                  <td className="text-sm text-gray-600 whitespace-nowrap">
                    {formatDateTime(release.published_at)}
                  </td>

                  {/* Actions */}
                  <td>
                    <button
                      onClick={() => {
                        if (confirm(`Eliminar versão v${release.version}?`)) {
                          deleteMutation.mutate(release.id)
                        }
                      }}
                      className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

